/** Per-user choice to list Start on site and Practical completion rows in the
 *  Home "Coming up" list.
 *
 *  Server-backed so the choice follows the person between browsers and the
 *  native shells. The localStorage copy is a first-paint cache, and the old
 *  per-browser key is read once so nobody who switched milestones on loses it. */

import { useCallback, useEffect, useState } from 'react';

import { API_BASE, getAuthHeaders } from '../utils/supabase/client';

const PREFERENCE_KEY = 'dashboard_coming_up_milestones';
const LEGACY_BROWSER_KEY = 'quantis.dashboard.comingUp.milestones';
const cacheKey = (userId: string) => `quantis:${PREFERENCE_KEY}:${userId}`;

/** A stored boolean wins; anything else (missing, null, junk) means off. */
export function resolveMilestonesPreference(value: unknown): boolean {
  return value === true;
}

/** The first account to load on a browser adopts the old per-browser choice and the
 *  old key is removed, so a later account on a shared browser does not inherit it. */
function readCache(userId: string): boolean {
  try {
    const own = localStorage.getItem(cacheKey(userId));
    if (own !== null) return own === 'true';
    if (localStorage.getItem(LEGACY_BROWSER_KEY) === null) return false;
    const adopted = localStorage.getItem(LEGACY_BROWSER_KEY) === '1';
    if (userId) {
      localStorage.setItem(cacheKey(userId), String(adopted));
      localStorage.removeItem(LEGACY_BROWSER_KEY);
    }
    return adopted;
  } catch {
    return false;
  }
}

function hasCache(userId: string): boolean {
  try { return localStorage.getItem(cacheKey(userId)) !== null; } catch { return false; }
}

function writeCache(userId: string, on: boolean): void {
  try {
    localStorage.setItem(cacheKey(userId), String(on));
  } catch { /* the cache is an optimisation, never a requirement */ }
}

// One queue per account at module level: the dashboard unmounts when the person
// navigates away, and a save still in flight must not be overtaken by one from
// the next mount.
const saveQueues = new Map<string, Promise<unknown>>();

function enqueueSave(userId: string, task: () => Promise<void>): Promise<void> {
  const next = (saveQueues.get(userId) ?? Promise.resolve()).then(task);
  saveQueues.set(userId, next.catch(() => {}));
  return next;
}

// Counts the person's own toggles per account, across mounts. A server read only
// applies if no newer toggle has happened since it began, which covers a toggle
// made before the first read lands, a toggle back to an earlier value, and a
// callback from a dashboard instance that has since been replaced.
const toggleCounts = new Map<string, number>();
const toggleCount = (userId: string) => toggleCounts.get(userId) ?? 0;

const endpoint = (userId: string) => `${API_BASE}/users/${userId}/preferences/${PREFERENCE_KEY}`;

async function saveToServer(userId: string, on: boolean): Promise<Response> {
  return fetch(endpoint(userId), {
    method: 'POST',
    headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ value: on }),
  });
}

/** The account's stored choice, or null when none is stored. Throws when it cannot be read. */
async function readFromServer(userId: string): Promise<boolean | null> {
  const res = await fetch(endpoint(userId), { headers: await getAuthHeaders() });
  // A failed read must not look like "nothing stored", or a stale cache would be
  // uploaded over a valid account value.
  if (!res.ok) throw new Error(`Failed to read preference (${res.status})`);
  const data = await res.json();
  return typeof data?.value === 'boolean' ? data.value : null;
}

export function useComingUpMilestonesPreference(userId: string): [boolean, (next: boolean) => void] {
  const [on, setOn] = useState<boolean>(() => readCache(userId));
  useEffect(() => {
    let cancelled = false;
    setOn(readCache(userId));
    if (!userId) return () => { cancelled = true; };

    (async () => {
      const startedAt = toggleCount(userId);
      try {
        // A save still in flight from an earlier mount must land before this read,
        // or the read returns the value from before it.
        await (saveQueues.get(userId) ?? Promise.resolve());
        const stored = await readFromServer(userId);
        if (cancelled || toggleCount(userId) !== startedAt) return;
        if (stored !== null) {
          setOn(stored);
          writeCache(userId, stored);
        } else if (hasCache(userId)) {
          // Nothing stored yet: a choice held only on this device (an earlier
          // per-browser one, or one made before the server knew the key) goes up.
          enqueueSave(userId, async () => { await saveToServer(userId, readCache(userId)); })
            .catch(() => { /* kept locally */ });
        }
      } catch {
        // Offline or a dead session: the cached value stands.
      }
    })();

    return () => { cancelled = true; };
  }, [userId]);

  const set = useCallback((next: boolean) => {
    const mine = toggleCount(userId) + 1;
    toggleCounts.set(userId, mine);
    setOn(next);
    writeCache(userId, next);
    if (!userId) return;
    enqueueSave(userId, async () => {
      const res = await saveToServer(userId, next);
      // A server that predates the key answers 404 and the choice stays on this
      // device. Any other failure falls through to the resync below.
      if (!res.ok && res.status !== 404) throw new Error(`Failed to save preference (${res.status})`);
    }).catch(async () => {
      // The save did not land, so show what the account really holds. If the
      // person has toggled again since, that newer save decides instead.
      try {
        const stored = await readFromServer(userId);
        if (stored === null || toggleCount(userId) !== mine) return;
        setOn(stored);
        writeCache(userId, stored);
      } catch { /* offline: the choice stays on this device */ }
    });
  }, [userId]);

  return [on, set];
}
